import {
  Component,
  effect,
  EventEmitter,
  inject,
  Injectable,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormGroup } from '@angular/forms';
import { AppUtilityService } from '../../../utility/app.utility';
import { RoutingService } from '../../../services/routing.service';
import { PermissionService } from '../../../services/permission.service';
import { ConfirmationDialogService } from '../../../services/confirmation-dialog.service';
import { getState } from '@ngrx/signals';
import { BookmarkCategoryHelperService } from '../../../services/bookmark-category/bookmark-category-helper.service';

@Component({
  selector: 'app-base-form',
  standalone: true,
  imports: [],
  templateUrl: './base-form.component.html',
  styleUrl: './base-form.component.scss',
})
@Injectable()
export abstract class BaseFormComponent<T extends { _id: string }>
  implements OnInit, OnChanges, OnDestroy
{
  abstract readonly store: any;
  private readonly baseFormBookmarkCategoryHelper = inject(BookmarkCategoryHelperService);
  form!: FormGroup;
  itemToEdit?: T;
  formInitialized = false;
  @Input() forceNew = false;
  @Input() cancelLevel = 1;
  @Input() disableCancelLink = false;
  private cancelLevelExplicitlySet = false;
  @Output() formSubmitted: EventEmitter<T | undefined> = new EventEmitter();
  @Output() cancel: EventEmitter<void> = new EventEmitter();

  protected constructor(
    protected router: Router,
    protected route: ActivatedRoute,
    protected appUtilityService: AppUtilityService,
    protected routingService: RoutingService,
    protected permissionService: PermissionService,
    protected confirmationDialogService: ConfirmationDialogService
  ) {
    effect(
      () => {
        const state: any = getState(this.store);

        if (
          state.selectedId &&
          this.store.selectedItem() &&
          !state.error &&
          !this.formInitialized
        ) {
          this.itemToEdit = this.forceNew
            ? this.store.selectedItem()
            : undefined;
          this.initializeForm();
        }

        if (state.error) {
          this.confirmationDialogService.addErrorMessage(state.error.message);
        } else if (state.action && state.lastPersistedId) {
          this.routingService.navigateBack(this.cancelLevel);
          this.formSubmitted.emit(this.form?.value);
        }

        if (this.formInitialized) {
          this.hasAccess();
        }
      },
      { allowSignalWrites: true }
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['cancelLevel']) {
      this.cancelLevelExplicitlySet = true;
    }
  }

  // eslint-disable-next-line @angular-eslint/contextual-lifecycle
  ngOnInit() {
    if (!this.cancelLevelExplicitlySet) {
      this.cancelLevel = this.routingService.getCancelLevel() ?? this.cancelLevel;
    }
    if (!this.formInitialized && this.store.selectedId()) {
      this.itemToEdit =
        this.store.selectedId() !== 'neu'
          ? this.store.selectedItem()
          : undefined;
      this.initializeForm();
    } else {
      this.itemToEdit = undefined;
      this.initializeForm();
    }
  }

  abstract initializeForm(): void;
  abstract onSubmit(): void;

  /**
   * Submits the form data. Updates or adds an item based on its state
   * and optionally navigates back to a previous route.
   *
   * @param {T} [overrideValue] - An optional value to override the form's current value.
   * @param {number} [navigateBack] - A flag specifying whether to navigate back after submission.
   *                                  If 0, no navigation occurs.
   * @return {void} This method does not return a value.
   */
  submit(overrideValue?: T, navigateBack?: number): void {
    if (this.form.valid) {
      if (this.itemToEdit && this.itemToEdit._id) {
        this.store.update({
          ...this.itemToEdit,
          ...(overrideValue || this.form.getRawValue()),
        });
      } else {
        this.store.addItem(overrideValue || this.form.getRawValue());
      }
    } else {
      this.form.markAllAsTouched();
      this.appUtilityService.markAllAsDirty(this.form);
    }
  }

  /**
   * confirm deletion using p-confirm module
   * @param event
   * @param item
   * @param itemName
   */
  confirm(event: Event | MouseEvent | undefined, item: T, itemName: string) {
    this.confirmationDialogService.confirmDeletion(
      event,
      itemName,
      this.store.entityLabel(),
      () => this.remove(item)
    );
  }

  /**
   * removes item by form
   * deselect item in store
   * @param item
   */
  protected remove = (item: T) => {
    this.store.deleteItem(item);
    this.store.deselect();
  };

  /**
   * Runs `cb` with the server-assigned id once a newly added entity has been
   * persisted (`action === 'add'` and `lastPersistedId` set) - for follow-up
   * actions that need a real id to exist first (e.g. persisting related state
   * that references the new entity). Only fires while `guard()` returns true,
   * so callers are expected to flip their own pending-flag back off inside
   * `cb` to avoid re-firing on the next store change.
   *
   * @param {() => boolean} guard - Whether a follow-up action is currently pending.
   * @param {(id: string) => void} cb - Runs once the new entity's id is available.
   */
  protected onceCreated(guard: () => boolean, cb: (id: string) => void): void {
    effect(() => {
      const state: any = getState(this.store);
      if (guard() && state.action === 'add' && state.lastPersistedId) {
        cb(state.lastPersistedId);
      }
    });
  }

  ngOnDestroy() {
    this.formInitialized = false;
    this.store.deselect();
    this.itemToEdit = undefined;
    this.formSubmitted.emit(this.form?.value);
  }

  /**
   * Checks the access permissions for the current entity type and triggers appropriate permission checks based on the entity type.
   *
   * The method determines the entity type stored and performs access validation by calling functions from the permissionService.
   * The specific check logic and permissions applied depend on the `entityType` and the selected entity's details.
   *
   * @return {void} Does not return any value.
   */
  hasAccess(): void {
    const entityType = this.store.entityType();
    const isAddMode = !this.itemToEdit;

    switch (entityType) {
      case 'Indicator':
        if (isAddMode) {
          // For add mode, check type-level permissions
          if (
            !this.permissionService.hasAddRightsByEntityType(
              'indicatorCategory'
            )
          ) {
            this.permissionService.blockPermission();
          }
        } else {
          // For edit mode, check entity-specific permissions
          this.permissionService.checkIndicatorRights(
            this.store.selectedItem()?.category,
            'edit',
            true
          );
        }
        break;
      case 'IndicatorCategory':
        if (isAddMode) {
          if (
            !this.permissionService.hasAddRightsByEntityType(
              'indicatorCategory'
            )
          ) {
            this.permissionService.blockPermission();
          }
        } else {
          this.permissionService.checkIndicatorRights(
            this.store.selectedItem()?._id,
            'edit',
            true
          );
        }
        break;
      case 'BookmarkCategory':
        if (isAddMode) {
          if (
            !this.permissionService.hasAddRightsByEntityType('bookmarkCategory')
          ) {
            this.permissionService.blockPermission();
          }
        } else {
          this.permissionService.checkBookmarkRights(
            this.store.selectedItem()?._id,
            'edit',
            true
          );
        }
        break;
      case 'Bookmark':
        if (isAddMode) {
          if (
            !this.permissionService.hasAddRightsByEntityType('bookmarkCategory')
          ) {
            this.permissionService.blockPermission();
          }
        } else {
          this.permissionService.checkBookmarkRights(
            this.baseFormBookmarkCategoryHelper.getCategoriesForBookmark(
              this.store.selectedItem()?._id || ''
            ),
            'edit',
            true
          );
        }
        break;
      case 'Story':
        if (isAddMode) {
          if (
            !this.permissionService.hasAddRightsByEntityType('bookmarkCategory')
          ) {
            this.permissionService.blockPermission();
          }
        } else {
          this.permissionService.checkBookmarkRights(
            this.store.selectedItem()?.category,
            'edit',
            true
          );
        }
        break;
      case 'DataLayerType':
        if (isAddMode) {
          // DataLayerType is a top-level entity, check both types
          if (
            !this.permissionService.hasAddRightsByEntityType('dataLayerType') &&
            !this.permissionService.hasAddRightsByEntityType('poiDataLayerType')
          ) {
            this.permissionService.blockPermission();
          }
        } else {
          this.permissionService.checkDataLayerRights(
            this.store.selectedItem()?._id,
            'edit',
            true
          );
        }
        break;
      case 'DataLayer':
        if (isAddMode) {
          // Check both types since we don't know which one yet
          if (
            !this.permissionService.hasAddRightsByEntityType('dataLayerType') &&
            !this.permissionService.hasAddRightsByEntityType('poiDataLayerType')
          ) {
            this.permissionService.blockPermission();
          }
        } else {
          this.permissionService.checkDataLayerRights(
            this.store.selectedItem()?.parent,
            'edit',
            true
          );
        }
        break;
      case 'IndicatorData':
        if (isAddMode) {
          if (
            !this.permissionService.hasAddRightsByEntityType(
              'indicatorCategory'
            )
          ) {
            this.permissionService.blockPermission();
          }
        } else {
          this.permissionService.checkIndicatorRights(
            this.store.selectedItem()?._id,
            'edit',
            true
          );
        }
        break;
      case 'Topic':
        if (isAddMode) {
          if (!this.permissionService.hasAddRightsByEntityType('topic')) {
            this.permissionService.blockPermission();
          }
        } else {
          this.permissionService.checkThemeRights(
            this.store.selectedItem()?._id,
            'edit',
            true
          );
        }
        break;
      case 'Workspace':
        if (!this.permissionService.adminBypass()) {
          this.permissionService.blockPermission();
        }
        break;
    }
  }
}
